import { AsyncButton } from "aic";
import { Modal } from "react-bootstrap";

export interface PopupAlertModalProps {
  id: string;
  show: boolean;
  title: string;
  message: string;
  onClose: () => void;
  preformatted?: boolean;
}

/**
 * Simple alert modal with title, message, and close button.
 *
 * @param props - The component props
 * @param props.id - Unique identifier for the modal
 * @param props.show - Whether the modal is currently displayed
 * @param props.title - Modal title text
 * @param props.message - Alert message to display
 * @param props.onClose - Callback invoked when modal is closed
 * @param props.preformatted - When true, renders the message in a `<pre>` block to preserve whitespace
 */
export const PopupAlertModal = ({
  id,
  show,
  title,
  message,
  onClose,
  preformatted = false,
}: PopupAlertModalProps): React.ReactElement => {
  return (
    <Modal
      id={id}
      show={show}
      onHide={onClose}
      size={preformatted ? "lg" : undefined}
    >
      <Modal.Header closeButton>
        <Modal.Title>{title}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {preformatted ? (
          <pre
            className="m-2 p-4 border border-primary rounded bg-primary bg-opacity-50"
            style={{
              maxHeight: "600px",
              overflow: "auto",
              whiteSpace: "pre-wrap",
              overflowWrap: "anywhere",
            }}
          >
            {message}
          </pre>
        ) : (
          <p>{message}</p>
        )}
      </Modal.Body>
      <Modal.Footer>
        <AsyncButton
          id={`${id}-close`}
          name="close"
          variant="secondary"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onClose();
          }}
        >
          Close
        </AsyncButton>
      </Modal.Footer>
    </Modal>
  );
};

PopupAlertModal.displayName = "PopupAlertModal";
